import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Zone definitions based on the exhibit mapping
export const ZONE_IDS = {
    ORANG_LAUT: '4.1', // ✅ Confirmed implemented - Orang Laut exhibit
    MARKET_TRADERS: '4.2', // ✅ Market Traders exhibit
    BADANG: '4.4', // Badang exhibit  
    BOOK_OF_MYTHS: '5.0', // Book of Myths exhibit
    PERANAKAN_DESIGN: '10.3', // Peranakan Design exhibit
    TIME_CAPSULE: '15.7', // Time Capsule exhibit
} as const;

export type ZoneId = typeof ZONE_IDS[keyof typeof ZONE_IDS];

// Redux-only zone status interface (with admin fields)
export interface ZoneStatusData {
    status: 'available' | 'processing' | 'error';
    claimedBy?: string;
    payload?: any;
    // Admin/debug fields
    zoneId?: string;
    updatedAt?: string;
}

export interface ZoneState {
    zones: Record<string, ZoneStatusData>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
    claimingZone: string | null;
    lastChecked: Record<string, number>;
    // Local session tracking for frontend-only mode
    userSessions: Record<string, { visitorId?: string; startTime: number; lastActivity: number }>;
}

const initialState: ZoneState = {
    zones: {},
    loading: {},
    error: {},
    claimingZone: null,
    lastChecked: {},
    userSessions: {},
};

// Helper function to generate session ID for anonymous users
const generateAnonymousSessionId = (): string => {
    return `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to check if a zone session is expired (5 minutes timeout)
const isSessionExpired = (session: { startTime: number; lastActivity: number }): boolean => {
    const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes
    return Date.now() - session.lastActivity > SESSION_TIMEOUT;
};

const zonesSlice = createSlice({
    name: 'zones',
    initialState,
    reducers: {
        // Initialize zone with default state
        initializeZone: (state, action: PayloadAction<{ zoneId: string }>) => {
            const { zoneId } = action.payload;
            if (!state.zones[zoneId]) {
                state.zones[zoneId] = {
                    status: 'available',
                    claimedBy: undefined,
                    payload: { option: 0 },
                    zoneId: zoneId,
                    updatedAt: new Date().toISOString()
                };
                state.loading[zoneId] = false;
                state.error[zoneId] = null;
                state.lastChecked[zoneId] = Date.now();
            }
        },

        // Get zone status (Redux-only, no API call)
        getZoneStatusStart: (state, action: PayloadAction<{ zoneId: string }>) => {
            const { zoneId } = action.payload;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
        },

        getZoneStatusSuccess: (state, action: PayloadAction<{ zoneId: string; visitorId?: string }>) => {
            const { zoneId, visitorId } = action.payload;
            
            // Initialize zone if it doesn't exist
            if (!state.zones[zoneId]) {
                state.zones[zoneId] = {
                    status: 'available',
                    claimedBy: undefined,
                    payload: { option: 0 },
                    zoneId: zoneId,
                    updatedAt: new Date().toISOString()
                };
            }

            // Check for expired sessions and clean them up
            Object.keys(state.userSessions).forEach(sessionZoneId => {
                if (isSessionExpired(state.userSessions[sessionZoneId])) {
                    delete state.userSessions[sessionZoneId];
                                         // Release the zone if it was claimed by this session
                     if (state.zones[sessionZoneId] && state.zones[sessionZoneId].status === 'processing') {
                         state.zones[sessionZoneId].status = 'available';
                         state.zones[sessionZoneId].claimedBy = undefined;
                         state.zones[sessionZoneId].payload = { option: 0 };
                         state.zones[sessionZoneId].updatedAt = new Date().toISOString();
                     }
                }
            });

            state.loading[zoneId] = false;
            state.lastChecked[zoneId] = Date.now();
        },

        getZoneStatusError: (state, action: PayloadAction<{ zoneId: string; error: string }>) => {
            const { zoneId, error } = action.payload;
            state.loading[zoneId] = false;
            state.error[zoneId] = error;
        },

        // Claim zone (Redux-only simulation)
        claimZoneStart: (state, action: PayloadAction<{ zoneId: string; visitorId?: string }>) => {
            const { zoneId } = action.payload;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
            state.claimingZone = zoneId;
        },

        claimZoneSuccess: (state, action: PayloadAction<{ zoneId: string; visitorId?: string }>) => {
            const { zoneId, visitorId } = action.payload;

            // Check if zone is available for claiming
            const currentZone = state.zones[zoneId];
            if (!currentZone || currentZone.status !== 'available') {
                // Zone is busy - simulate API failure
                state.loading[zoneId] = false;
                state.claimingZone = null;
                state.error[zoneId] = 'Zone is currently busy';
                return;
            }

            // Create session ID (use visitorId if available, otherwise generate anonymous ID)
            const sessionId = visitorId || generateAnonymousSessionId();

            // Claim the zone
            state.zones[zoneId] = {
                status: 'processing',
                claimedBy: sessionId,
                payload: { option: 0 },
                zoneId: zoneId,
                updatedAt: new Date().toISOString()
            };

            // Track user session for this zone
            state.userSessions[zoneId] = {
                visitorId: visitorId,
                startTime: Date.now(),
                lastActivity: Date.now()
            };

            state.loading[zoneId] = false;
            state.claimingZone = null;
            state.lastChecked[zoneId] = Date.now();

            console.log(`✅ Zone ${zoneId} claimed successfully by ${sessionId} (Frontend-only mode)`);
        },

        claimZoneError: (state, action: PayloadAction<{ zoneId: string; error: string }>) => {
            const { zoneId, error } = action.payload;
            state.loading[zoneId] = false;
            state.error[zoneId] = error;
            state.claimingZone = null;
        },

        // Update zone payload (Redux-only simulation)
        updateZonePayloadStart: (state, action: PayloadAction<{ zoneId: string }>) => {
            const { zoneId } = action.payload;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
        },

        updateZonePayloadSuccess: (state, action: PayloadAction<{ 
            zoneId: string; 
            payload: any; 
            visitorId?: string;
        }>) => {
            const { zoneId, payload, visitorId } = action.payload;

            // Verify user has permission to update this zone
            const currentZone = state.zones[zoneId];
            const userSession = state.userSessions[zoneId];

            if (!currentZone || !userSession) {
                state.loading[zoneId] = false;
                state.error[zoneId] = 'Zone not claimed or session expired';
                return;
            }

            // Check if the requesting user matches the zone claimer
            const sessionId = visitorId || (userSession.visitorId ? userSession.visitorId : state.zones[zoneId].claimedBy);
            if (currentZone.claimedBy !== sessionId) {
                state.loading[zoneId] = false;
                state.error[zoneId] = 'Not authorized to update this zone';
                return;
            }

            // Update zone payload
            state.zones[zoneId].payload = { ...state.zones[zoneId].payload, ...payload };
            state.zones[zoneId].updatedAt = new Date().toISOString();
            
            // Update last activity time
            state.userSessions[zoneId].lastActivity = Date.now();
            
            state.loading[zoneId] = false;
            state.lastChecked[zoneId] = Date.now();

            console.log(`✅ Zone ${zoneId} payload updated to:`, payload, '(Frontend-only mode)');
        },

        updateZonePayloadError: (state, action: PayloadAction<{ zoneId: string; error: string }>) => {
            const { zoneId, error } = action.payload;
            state.loading[zoneId] = false;
            state.error[zoneId] = error;
        },

        // Update zone status (Redux-only simulation)
        updateZoneStatusStart: (state, action: PayloadAction<{ zoneId: string }>) => {
            const { zoneId } = action.payload;
            state.loading[zoneId] = true;
            state.error[zoneId] = null;
        },

        updateZoneStatusSuccess: (state, action: PayloadAction<{ 
            zoneId: string; 
            statusData: ZoneStatusData; 
            visitorId?: string;
        }>) => {
            const { zoneId, statusData, visitorId } = action.payload;

            // Verify user has permission (for setting to 'available', allow anyone to release)
            const currentZone = state.zones[zoneId];
            const userSession = state.userSessions[zoneId];

                         if (statusData.status === 'available') {
                 // Allow releasing the zone
                 state.zones[zoneId] = {
                     status: 'available',
                     claimedBy: undefined,
                     payload: { option: 0 },
                     zoneId: zoneId,
                     updatedAt: new Date().toISOString()
                 };

                // Clear user session
                if (state.userSessions[zoneId]) {
                    delete state.userSessions[zoneId];
                }
            } else {
                // For other status updates, verify permission
                if (!currentZone || !userSession) {
                    state.loading[zoneId] = false;
                    state.error[zoneId] = 'Zone not claimed or session expired';
                    return;
                }

                const sessionId = visitorId || (userSession.visitorId ? userSession.visitorId : state.zones[zoneId].claimedBy);
                if (currentZone.claimedBy !== sessionId) {
                    state.loading[zoneId] = false;
                    state.error[zoneId] = 'Not authorized to update this zone';
                    return;
                }

                                 // Update zone status
                 state.zones[zoneId] = { 
                     ...state.zones[zoneId], 
                     ...statusData,
                     zoneId: zoneId,
                     updatedAt: new Date().toISOString()
                 };
                 
                 // Update last activity time
                 state.userSessions[zoneId].lastActivity = Date.now();
            }
            
            state.loading[zoneId] = false;
            state.lastChecked[zoneId] = Date.now();

            console.log(`✅ Zone ${zoneId} status updated to:`, statusData.status, '(Frontend-only mode)');
        },

        updateZoneStatusError: (state, action: PayloadAction<{ zoneId: string; error: string }>) => {
            const { zoneId, error } = action.payload;
            state.loading[zoneId] = false;
            state.error[zoneId] = error;
        },

        // Clear zone error
        clearZoneError: (state, action: PayloadAction<string>) => {
            const zoneId = action.payload;
            state.error[zoneId] = null;
        },

        // Clear all zone errors
        clearAllZoneErrors: (state) => {
            state.error = {};
        },

        // Update last checked timestamp
        setZoneLastChecked: (state, action: PayloadAction<{ zoneId: string; timestamp: number }>) => {
            const { zoneId, timestamp } = action.payload;
            state.lastChecked[zoneId] = timestamp;
        },

        // Clean up expired sessions (can be called periodically)
        cleanupExpiredSessions: (state) => {
            Object.keys(state.userSessions).forEach(zoneId => {
                if (isSessionExpired(state.userSessions[zoneId])) {
                    console.log(`🧹 Cleaning up expired session for zone ${zoneId}`);
                    delete state.userSessions[zoneId];
                    
                                         // Release the zone if it was claimed by this session
                     if (state.zones[zoneId] && state.zones[zoneId].status === 'processing') {
                         state.zones[zoneId].status = 'available';
                         state.zones[zoneId].claimedBy = undefined;
                         state.zones[zoneId].payload = { option: 0 };
                         state.zones[zoneId].updatedAt = new Date().toISOString();
                     }
                }
            });
        },

        // Reset all zones (useful for testing or admin reset)
        resetAllZones: (state) => {
            state.zones = {};
            state.loading = {};
            state.error = {};
            state.claimingZone = null;
            state.userSessions = {};
            console.log('🔄 All zones reset to initial state (Frontend-only mode)');
        }
    },
});

export const {
    initializeZone,
    getZoneStatusStart,
    getZoneStatusSuccess,
    getZoneStatusError,
    claimZoneStart,
    claimZoneSuccess,
    claimZoneError,
    updateZonePayloadStart,
    updateZonePayloadSuccess,
    updateZonePayloadError,
    updateZoneStatusStart,
    updateZoneStatusSuccess,
    updateZoneStatusError,
    clearZoneError,
    clearAllZoneErrors,
    setZoneLastChecked,
    cleanupExpiredSessions,
    resetAllZones
} = zonesSlice.actions;

export default zonesSlice.reducer; 